<script setup lang="ts">
    import { ref, computed, toRef } from 'vue'

    import { KendoChart } from '@Kendo/Components/KendoChart'
    import type { RadarChartDialogViewModel } from '@Evaluation/ValueAnalysis/Components/RadarChartDialog'
    import type { CriterionReadModel } from '../Domain/readmodels'

    // ---------------- props ----------------

    const props = defineProps<{
        viewModel: RadarChartDialogViewModel
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- local state ----------------

    const topLevel = ref(true)

    const themes = [
        'default',
        'black',
        'blueopal',
        'bootstrap',
        'highcontrast',
        'metro',
        'metroblack',
        'moonlight',
        'silver',
        'uniform'
    ]

    // ---------------- computed ----------------

    const opened = computed(() => vm.value?.opened ?? false)

    const theme = computed<string>({
        get: () => vm.value.theme,
        set: val => { vm.value.theme = val }
    })

    const selectedCriterionName = computed<string | null>(() =>
        vm.value.selectedCriterion?.Name ?? null
    )

    const chart = computed<KendoChart>(() => vm.value.chart)

    const criteria = computed<CriterionReadModel[]>(() => vm.value.criteria)

    const parents = computed<CriterionReadModel[]>(() =>
        criteria.value.filter(c => c.IsParent === true)
    )

    // ---------------- actions ----------------

    function handleChangeCriterion(criterionId: number)
    {
        if (criterionId !== vm.value.criterionId)
        {
            vm.value.criterionId = criterionId
            vm.value.setChartData(topLevel.value)
        }
    }

    function handleChangeTheme(t: string)
    {
        if (t !== theme.value)
        {
            theme.value = t
            chart.value.SetTheme(t)
        }
    }

    function handleToggleLevel()
    {
        topLevel.value = !topLevel.value
        vm.value.setChartData(topLevel.value)
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div>
        <div class="modal-mask" v-if="opened">
            <div class="modal-wrapper">
                <div class="modal-dialog modal-xl modal-dialog-scrollable">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">Radar chart</h5>
                            <button type="button" class="close" @click="close">&times;</button>
                        </div>
                        <div class="modal-body">
                            <div>
                                <div class="dropdown d-inline-block mr-1">
                                    <button class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                        {{selectedCriterionName}}
                                    </button>
                                    <div class="dropdown-menu" style="max-height: 300px; overflow-y:auto;">
                                        <a class="dropdown-item" v-for="c in parents" :key="c.Id" @click="handleChangeCriterion(c.Id)">
                                            {{c.Name}}
                                        </a>
                                    </div>
                                </div>

                                <button class="btn btn-outline-secondary mr-1" @click="handleToggleLevel">
                                    <i class="fa-regular fa-fw" :class="topLevel ? 'fa-square' : 'fa-square-check'"></i>
                                    Bottom level
                                </button>

                                <div class="dropdown d-inline-block">
                                    <button class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">{{theme}}</button>
                                    <div class="dropdown-menu">
                                        <h6 class="dropdown-header">Color theme:</h6>
                                        <a class="dropdown-item" v-for="t in themes" :key="t" @click="handleChangeTheme(t)">{{t}}</a>
                                    </div>
                                </div>
                            </div>

                            <br />

                            <div style="max-height: calc(100vh-200px); overflow: auto;">
                                <div id="Chart-RadarChart"></div>
                            </div>
                        </div>

                        <div class="modal-footer">
                            <button @click="close" type="button" class="btn btn-outline-secondary">Close</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>